import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Modal } from "../../../components/Modal"
import { DiffType, SampleComparisonColumn, SamplesContext, SamplesResponseType, UpdateSamplesContext, sampleMeasurements } from "../../../context/samplesContext"
import { formatNumber, formatOrdinal, MEASURE_COLOR_LIST } from "../../utils/helper"
import { useTableContext } from "../../../context/tableContext"
import clsx from "clsx"
import { getResolutionFromSample } from "../../utils/resolution"
import { markLegacyNormalized } from "../utils/manualLegacy"
import debounce from "lodash/debounce"
import { useCanEdit } from "../../../hooks/useCanEdit"
import { SelectedImageInfo } from "../types"
import { useTranslation } from "react-i18next"
import {
  MANUAL_START_INDEX,
  MeasurementTableRow,
  getTableRowBackgroundClass,
  isMeasurementRowDrawing,
  mapMeasurementTableData,
  planManualMeasurementForCell,
} from "./measurementTableLogic"

type DataType = MeasurementTableRow

const FROZEN_COLUMN_WIDTHS = {
  no: 31,
  color: 40,
  name: 117,
  delete: 70,
} as const

const FROZEN_COLUMN_LEFTS = {
  no: 0,
  color: FROZEN_COLUMN_WIDTHS.no,
  name: FROZEN_COLUMN_WIDTHS.no + FROZEN_COLUMN_WIDTHS.color,
  delete: FROZEN_COLUMN_WIDTHS.no + FROZEN_COLUMN_WIDTHS.color + FROZEN_COLUMN_WIDTHS.name,
} as const

const FROZEN_COLUMNS_WIDTH = FROZEN_COLUMN_WIDTHS.no
  + FROZEN_COLUMN_WIDTHS.color
  + FROZEN_COLUMN_WIDTHS.name
  + FROZEN_COLUMN_WIDTHS.delete
const SPEC_COLUMN_WIDTH = 70
const RESULT_COLUMN_WIDTH = 70
const DIFF_COLUMN_WIDTH = 52
const HEADER_FIRST_ROW_HEIGHT = 30
const ROW_HEIGHT = 36
const HEADER_STICKY_Z_INDEX = 10
const FROZEN_HEADER_STICKY_Z_INDEX =21
const FROZEN_BODY_STICKY_Z_INDEX = 20
const BODY_CELL_BORDER_STYLE = {
  borderRight: '1px solid #adadad',
  borderBottom: '1px solid #3E3E3E',
} as const
const FIRST_BODY_CELL_BORDER_STYLE = {
  borderLeft: '1px solid #adadad',
  borderBottom: '1px solid #3E3E3E',
  borderRight: '1px solid #3E3E3E',
} as const
const TABLE_HEADER_LABEL_BASE_CLASS = "flex h-full w-full items-center justify-center overflow-hidden break-words text-center"
const TABLE_HEADER_LABEL_EN_CLASS = `${TABLE_HEADER_LABEL_BASE_CLASS} px-[1px] text-[9px] leading-none`
const TABLE_HEADER_LABEL_JA_CLASS = `${TABLE_HEADER_LABEL_BASE_CLASS} px-[3px]`

const fixedWidthStyle = (width: number): React.CSSProperties => ({
  width,
  minWidth: width,
  maxWidth: width,
})

const stickyStyle = ({ left, top, zIndex }: { left?: number, top?: number, zIndex: number }): React.CSSProperties => ({
  position: 'sticky',
  ...(typeof left === 'number' ? { left } : {}),
  ...(typeof top === 'number' ? { top } : {}),
  zIndex,
})

const getComparisonGroupWidth = (column: SampleComparisonColumn, currentColumnKey: string | null): number => {
  return SPEC_COLUMN_WIDTH
    + RESULT_COLUMN_WIDTH
    + (column.key === currentColumnKey ? DIFF_COLUMN_WIDTH : 0)
}

const getFallbackTableContentWidth = (hasSpec: boolean): number => {
  return FROZEN_COLUMNS_WIDTH
    + (hasSpec ? SPEC_COLUMN_WIDTH : 0)
    + RESULT_COLUMN_WIDTH
    + DIFF_COLUMN_WIDTH
}

export const getMeasurementTableContentWidth = ({
  comparisonColumns,
  currentColumnKey,
  hasComparison,
  hasSpec,
}: {
  comparisonColumns: SampleComparisonColumn[]
  currentColumnKey: string | null
  hasComparison: boolean
  hasSpec: boolean
}): number => {
  if (!hasComparison) {
    return getFallbackTableContentWidth(hasSpec)
  }

  return FROZEN_COLUMNS_WIDTH + comparisonColumns.reduce(
    (total, column) => total + getComparisonGroupWidth(column, currentColumnKey),
    0
  )
}

export const getMeasurementTableViewportWidth = (options: {
  comparisonColumns: SampleComparisonColumn[]
  currentColumnKey: string | null
  hasComparison: boolean
  hasSpec: boolean
}): number => {
  return getMeasurementTableContentWidth(options)
}

const isZeroDiffValue = (diffValue: DataType['diff']): boolean => {
  if (typeof diffValue === 'number') {
    return diffValue === 0
  }

  if (typeof diffValue !== 'string') {
    return false
  }

  const trimmedDiffValue = diffValue.trim()
  if (trimmedDiffValue === '' || trimmedDiffValue === '-') {
    return false
  }

  const parsedDiffValue = Number(trimmedDiffValue.replace(/,/g, ''))
  return Number.isFinite(parsedDiffValue) && parsedDiffValue === 0
}

const formatNullableValue = (value: number | string | null | undefined): number | string => {
  if (value === null || value === undefined || value === 0) {
    return ''
  }
  return value
}

const getColumnLabel = (column: SampleComparisonColumn): string => {
  if (column.label) return column.label
  return column.kind === 'final' ? 'FINAL' : formatOrdinal(column.time)
}

const shouldDimMeasurementRow = ({
  hasMultipleSampleImages,
  selectedImage,
  rowImageIndex,
  hasCurrentMeasurement,
}: {
  hasMultipleSampleImages: boolean
  selectedImage: SelectedImageInfo | null
  rowImageIndex: number | null
  hasCurrentMeasurement: boolean
}): boolean => {
  if (!hasMultipleSampleImages) return false
  if (selectedImage?.source !== 'sample') return false
  if (!hasCurrentMeasurement) return false
  if (typeof rowImageIndex !== 'number') return false
  return rowImageIndex !== selectedImage.image.image_index
}

export function TableContainer({
  setManualDrawingIndex,
  manualDrawingIndex,
  selectedImage,
}: {
  setManualDrawingIndex: React.Dispatch<React.SetStateAction<number | null>>
  manualDrawingIndex: number | null
  selectedImage: SelectedImageInfo | null
}) {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language.startsWith('en')
  const firstRowHeaderLabelClass = isEnglish ? TABLE_HEADER_LABEL_EN_CLASS : `${TABLE_HEADER_LABEL_JA_CLASS} text-[12px] leading-normal`
  const secondRowHeaderLabelClass = isEnglish ? TABLE_HEADER_LABEL_EN_CLASS : `${TABLE_HEADER_LABEL_JA_CLASS} text-[14px] leading-normal`
  const { rowSample, comparison, diff } = useContext(SamplesContext)
  const { updateRowSample, fetchDiff } = useContext(UpdateSamplesContext)
  const { setColorMap, setIndexMap } = useTableContext()
  const { canEdit } = useCanEdit()

  // inline編集用State
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  // 重複確認モーダルのステート
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pending, setPending] = useState<{ index: number, newName: string, duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null>(null)

  const [displayDiff, setDisplayDiff] = useState<DiffType | null>(null)
  const [tableData, setTableData] = useState<DataType[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastInitialScrollKeyRef = useRef<string | null>(null)

  // AutoEditConfirmModal
  const [isAutoConfirmOpen, setIsAutoConfirmOpen] = useState(false)
  const [autoPendingCell, setAutoPendingCell] = useState<DataType | null>(null)

  const [resolution, setResolution] = useState<number>(0)

  const comparisonColumns = useMemo(
    () => comparison?.columns ?? [],
    [comparison]
  )
  const hasMultipleSampleImages = useMemo(
    () => (rowSample.sampleImages?.length ?? 0) > 1,
    [rowSample.sampleImages]
  )
  const hasComparison = comparisonColumns.length > 0 && Boolean(comparison?.rows?.length)
  const currentColumnKey = useMemo(() => {
    const currentColumn = comparisonColumns.find(column => column.is_current || column.sample_id === rowSample.id)
    return currentColumn?.key ?? null
  }, [comparisonColumns, rowSample.id])
  const tableContentWidth = useMemo(() => {
    return getMeasurementTableContentWidth({
      comparisonColumns,
      currentColumnKey,
      hasComparison,
      hasSpec: Boolean(displayDiff?.data?.condition.hasSpec),
    })
  }, [comparisonColumns, currentColumnKey, displayDiff?.data?.condition.hasSpec, hasComparison])

  useEffect(() => {
    if (!hasComparison || !currentColumnKey || !rowSample.id) return

    const initialScrollKey = `${rowSample.id}:${currentColumnKey}`
    if (lastInitialScrollKeyRef.current === initialScrollKey) return

    const container = scrollContainerRef.current
    if (!container) return

    const animationFrameId = window.requestAnimationFrame(() => {
      lastInitialScrollKeyRef.current = initialScrollKey
      container.scrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0)
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [currentColumnKey, hasComparison, rowSample.id, tableContentWidth])

  useEffect(() => {
    if (!rowSample?.id || rowSample?.id === '') return // 未ロード時
    try {
      const r = getResolutionFromSample(rowSample)
      setResolution(r)
    } catch (_e) {
      console.error('Resolution determination failed:', _e)
      alert(t('messages.resolutionUnknown'))
      setResolution(0)
    }
  }, [rowSample?.id, rowSample?.sampleMeasurements, t])

  // diff 更新を反映
  useEffect(() => {
    setDisplayDiff(diff)
  }, [diff])

  const debouncedFetchDiff = useMemo(
    () =>
      debounce((sample: SamplesResponseType) => {
        fetchDiff(sample)
      }, 500),
    [fetchDiff]
  )

  useEffect(() => {
    return () => {
      debouncedFetchDiff.cancel()
    }
  }, [debouncedFetchDiff])

  // fetchDiff のデバウンス
  useEffect(() => {
    const { part_number, sample_number, repeat, kind, time, size } = rowSample
    const isIncomplete =
      (!part_number && !sample_number) ||
      repeat === null ||
      repeat === undefined ||
      (kind === 'sample' && (time === null || time === undefined)) ||
      size === null ||
      size === undefined

    if (isIncomplete) {
      setDisplayDiff(null)
      debouncedFetchDiff.cancel()
      return
    }

    debouncedFetchDiff(rowSample)
  }, [debouncedFetchDiff, rowSample])

  // テーブル表示用データを構築（純粋関数 mapMeasurementTableData に委譲）
  const mapTableData = useCallback((): DataType[] => {
    return mapMeasurementTableData({
      sampleMeasurements: rowSample.sampleMeasurements,
      hasComparison,
      comparison,
      comparisonColumns,
      currentColumnKey,
      displayDiff,
    })
  }, [rowSample.sampleMeasurements, hasComparison, comparison, comparisonColumns, currentColumnKey, displayDiff])

  useEffect(() => {
    const data = mapTableData()
    setTableData(data)

    const cm = new Map<number, string>()
    const im = new Map<number, number>()
    data.forEach((row, i) => {
      const displayMapIndex = row.data_measure_index ?? row.candidate_measure_index
      cm.set(displayMapIndex, MEASURE_COLOR_LIST[i].color)
      im.set(displayMapIndex, row.measure_index)
    })
    setColorMap(cm)
    setIndexMap(im)
  }, [rowSample, displayDiff, mapTableData, setColorMap, setIndexMap])

  const onNameClick = (cell: DataType) => {
    if (!canEdit) return
    if (cell.measure_type === 'auto') return
    setEditingRowKey(cell.row_key)
    setEditingName(cell.measure_name)
  }

  const applyNameChange = (cell: DataType, name: string) => {
    if (!name.trim()) {
      setEditingRowKey(null)
      return
    }
    const idx = cell.data_measure_index ?? cell.candidate_measure_index
    const dupIdx = rowSample.sampleMeasurements.findIndex(
      (m) =>
        m.measure_name === name &&
        !m.is_deleted &&
        m.measure_index !== idx
    )
    if (dupIdx !== -1) {
      const dup = rowSample.sampleMeasurements[dupIdx]
      setPending({
        index: idx,
        newName: name,
        duplicateIdx: dupIdx,
        duplicateType: dup.measure_type,
      })
      setConfirmOpen(true)
    } else {
      commitChange(idx, name, null)
    }
  }

  const commitChange = (idx: number, name: string, pendingInfo: { duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null) => {
    // 1. 元データをコピー
    let updated = [...rowSample.sampleMeasurements]
    const autoData = updated.find((m) => m.measure_type === 'auto')
    const resolveNewManualIndex = () => {
      const usedMeasurementIndexes = new Set(updated.map(m => m.measure_index))
      if (idx >= MANUAL_START_INDEX && !usedMeasurementIndexes.has(idx)) {
        return idx
      }

      const existing = [
        ...updated.map(m => m.measure_index),
        ...tableData.map(row => row.candidate_measure_index),
      ]
      const maxIdx = existing.length ? Math.max(...existing) : 0
      return Math.max(maxIdx + 1, MANUAL_START_INDEX)
    }
    const resolveSelectedSampleImageIndex = () => {
      const selectedSampleImageIndex = selectedImage?.source === 'sample'
        ? selectedImage.image.image_index
        : null
      if (typeof selectedSampleImageIndex !== 'number' || selectedSampleImageIndex < 1) {
        alert(t('messages.selectMeasurementImageToAdd'))
        setEditingRowKey(null)
        setConfirmOpen(false)
        return null
      }
      return selectedSampleImageIndex
    }

    if (pendingInfo) {
      const { duplicateIdx, duplicateType } = pendingInfo
      const originalIndex = updated[duplicateIdx].measure_index

      // 2. auto->manual　上書きのときはautoを論理削除
      if (duplicateType === 'auto') {
        updated = updated.map((m, i) =>
          i === duplicateIdx && m.measure_type === 'auto'
            ? { ...m, is_deleted: true }
            : m
        )
        const manualAtIdx = updated.find(
          m => m.measure_type === 'manual' && m.measure_index === idx
        )
        if (manualAtIdx) {
          updated = updated.filter((m) => m !== manualAtIdx)
          {
            const manualEntry: sampleMeasurements = {
              ...manualAtIdx,
              measure_index: originalIndex,
              measure_name: name,
            }
            markLegacyNormalized(manualEntry)
            updated.push(manualEntry)
          }
        } else {
          const selectedSampleImageIndex = resolveSelectedSampleImageIndex()
          if (selectedSampleImageIndex === null) return
          const manualEntry: sampleMeasurements = {
            measure_index: originalIndex,
            measure_name: name,
            image_index: selectedSampleImageIndex,
            resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
            // resolution: autoData?.resolution as number, // FIXME: 暫定対応
            result_pixel: 0,
            result_real: 0,
            result_point: [],
            measure_type: 'manual',
            time: rowSample.sampleMeasurements[0]?.time,
            is_deleted: false,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        }
      }

      if (duplicateType === 'manual') {
        updated = updated.filter(
          m => !(m.measure_type === 'manual' && m.measure_index === originalIndex)
        )
        const manualAtIdx = updated.find(
          m => m.measure_type === 'manual' && m.measure_index === idx
        )
        const autoData = updated.find((m) => m.measure_type === 'auto')

        if (manualAtIdx) {
          updated = updated.filter(m => m !== manualAtIdx)
          const manualEntry: sampleMeasurements = {
            ...manualAtIdx,
            measure_index: originalIndex,
            measure_name: name,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        } else {
          const selectedSampleImageIndex = resolveSelectedSampleImageIndex()
          if (selectedSampleImageIndex === null) return
          const manualEntry: sampleMeasurements = {
            measure_index: originalIndex,
            measure_name: name,
            image_index: selectedSampleImageIndex,
            resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
            // resolution: autoData?.resolution as number, // FIXME: 暫定対応
            result_pixel: 0,
            result_real: 0,
            result_point: [],
            measure_type: 'manual',
            time: rowSample.sampleMeasurements[0]?.time,
            is_deleted: false,
          }
          markLegacyNormalized(manualEntry)
          updated.push(manualEntry)
        }
      }
    } else {
      // ── 重複なし：そのセルの manual があれば名前更新、なければ追加 ──
      const exIdx = updated.findIndex(m => m.measure_index === idx && m.measure_type === "manual")

      if (exIdx >= 0) {
        updated[exIdx] = { ...updated[exIdx], measure_name: name }
      } else {
        const selectedSampleImageIndex = resolveSelectedSampleImageIndex()
        if (selectedSampleImageIndex === null) return
        const newOriginal = resolveNewManualIndex()

        const manualEntry: sampleMeasurements = {
          measure_index: newOriginal,
          measure_name: name,
          image_index: selectedSampleImageIndex,
          resolution: resolution || autoData?.resolution as number, // FIXME: 暫定対応
          // resolution: autoData?.resolution as number, // FIXME: 暫定対応
          result_pixel: 0,
          result_real: 0,
          result_point: [],
          measure_type: 'manual',
          time: rowSample.sampleMeasurements[0].time,
          is_deleted: false,
        }
        markLegacyNormalized(manualEntry)
        updated.push(manualEntry)
      }
    }

    updateRowSample({ sampleMeasurements: updated })
    setEditingRowKey(null)
    setConfirmOpen(false)
  }

  const onConfirmOK = () => {
    if (pending) {
      commitChange(pending.index, pending.newName, {
        duplicateIdx: pending.duplicateIdx,
        duplicateType: pending.duplicateType
      })
      setPending(null)
    }
  }

  const onConfirmCancel = () => {
    setPending(null)
    setConfirmOpen(false)
    setEditingRowKey(null)
  }

  // 削除
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMeasure, setDeleteMeasure] = useState<DataType>()
  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])
  const handleDeleteClick = useCallback((cell: DataType) => {
    if (!canEdit) return
    setDeleteMeasure(cell)
    openModal()
  }, [canEdit, openModal])

  const ensureManualMeasurementForCell = useCallback((cell: DataType) => {
    const selectedSampleImageIndex = selectedImage?.source === 'sample'
      ? selectedImage.image.image_index
      : null
    const plan = planManualMeasurementForCell({
      cell,
      sampleMeasurements: rowSample.sampleMeasurements,
      tableCandidateIndexes: tableData.map(row => row.candidate_measure_index),
      selectedSampleImageIndex,
      resolution,
      sampleTime: rowSample.time,
    })
    if (plan.kind === 'blocked_no_image') {
      alert(t('messages.loadMeasurementImageBeforeManual'))
      return null
    }
    if (plan.kind === 'use_index') {
      return plan.measureIndex
    }
    // create: mark → update の順は従来どおり
    markLegacyNormalized(plan.entry)
    updateRowSample({ sampleMeasurements: [...rowSample.sampleMeasurements, plan.entry] })
    return plan.measureIndex
  }, [resolution, rowSample, selectedImage, t, tableData, updateRowSample])

  // 編集
  const handleResultCellClick = useCallback((cell: DataType) => {
    if (!canEdit) return

    if (cell.measure_type === 'auto') {
      setAutoPendingCell(cell)
      setIsAutoConfirmOpen(true)
    } else {
      const manualMeasureIndex = ensureManualMeasurementForCell(cell)
      if (manualMeasureIndex === null) return
      setManualDrawingIndex(manualMeasureIndex)
    }
  }, [canEdit, ensureManualMeasurementForCell, setManualDrawingIndex])

  const onAutoConfirmOK = useCallback(() => {
    if (!autoPendingCell || autoPendingCell.data_measure_index === null) return
    const idx = autoPendingCell.data_measure_index

    const autoData = rowSample.sampleMeasurements.find(
      m => m.measure_index === idx && m.measure_type === 'auto'
    )
    let updated = rowSample.sampleMeasurements.map(m =>
      m.measure_index === idx && m.measure_type === 'auto'
        ? { ...m, is_deleted: true }
        : m
    )

    const manualEntry: sampleMeasurements = {
      measure_index: idx,
      measure_name: autoData?.measure_name!,
      image_index: autoData?.image_index!,
      resolution: resolution || autoData?.resolution!, // FIXME: 暫定対応
      // resolution: autoData?.resolution!, // FIXME: 暫定対応
      result_pixel: autoData?.result_pixel!,
      result_real: autoData?.result_real!,
      result_point: autoData?.result_point!,
      measure_type: 'manual',
      time: autoData?.time!,
      is_deleted: false,
    }
    markLegacyNormalized(manualEntry)
    updated.push(manualEntry)

    updateRowSample({ sampleMeasurements: updated })
    setManualDrawingIndex(idx)

    setIsAutoConfirmOpen(false)
    setAutoPendingCell(null)
  }, [autoPendingCell, rowSample, updateRowSample, setManualDrawingIndex, resolution])

  const onAutoConfirmCancel = useCallback(() => {
    setIsAutoConfirmOpen(false)
    setAutoPendingCell(null)
  }, [])

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-[387px] overflow-auto text-white desktop-xmd:h-[615px]"
      style={{
        maxWidth: '100%',
      }}
    >
      <table className="border-separate border-spacing-0 table-fixed" style={{ width: '100%', minWidth: tableContentWidth }}>
        <colgroup>
          <col style={fixedWidthStyle(FROZEN_COLUMN_WIDTHS.no)} />
          <col style={fixedWidthStyle(FROZEN_COLUMN_WIDTHS.color)} />
          <col style={fixedWidthStyle(FROZEN_COLUMN_WIDTHS.name)} />
          <col style={fixedWidthStyle(FROZEN_COLUMN_WIDTHS.delete)} />
          {hasComparison ? (
            comparisonColumns.map(column => (
              <React.Fragment key={column.key}>
                <col style={fixedWidthStyle(SPEC_COLUMN_WIDTH)} />
                <col style={fixedWidthStyle(RESULT_COLUMN_WIDTH)} />
                {column.key === currentColumnKey && (
                  <col style={fixedWidthStyle(DIFF_COLUMN_WIDTH)} />
                )}
              </React.Fragment>
            ))
          ) : (
            <>
              {displayDiff?.data?.condition.hasSpec && <col style={fixedWidthStyle(SPEC_COLUMN_WIDTH)} />}
              <col style={fixedWidthStyle(RESULT_COLUMN_WIDTH)} />
              <col style={fixedWidthStyle(DIFF_COLUMN_WIDTH)} />
            </>
          )}
        </colgroup>
        <thead>
          <tr className="text-[11px] leading-[1.15] [&>th]:font-medium">
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] h-[53px] sticky
              before:content-[''] before:absolute before:inset-0
              before:border before:border-[#adadad]"
              style={{
                ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.no),
                ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.no, top: 0, zIndex: FROZEN_HEADER_STICKY_Z_INDEX }),
              }}
            >
              <span className={firstRowHeaderLabelClass}>No.</span>
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
              style={{
                ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.color),
                ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.color, top: 0, zIndex: FROZEN_HEADER_STICKY_Z_INDEX }),
              }}
            >
              <span className={firstRowHeaderLabelClass}>{t('drawing.lineColor')}</span>
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
              style={{
                ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.name),
                ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.name, top: 0, zIndex: FROZEN_HEADER_STICKY_Z_INDEX }),
              }}
            >
              <span className={firstRowHeaderLabelClass}>{t('drawing.measurementPoint')}</span>
            </th>
            <th
              rowSpan={2}
              className="bg-[#3E3E3E] sticky
              before:content-[''] before:absolute before:inset-0
              before:border-t before:border-b before:border-r before:border-[#adadad]"
              style={{
                ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.delete),
                ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.delete, top: 0, zIndex: FROZEN_HEADER_STICKY_Z_INDEX }),
              }}
            >
              <span className={firstRowHeaderLabelClass}>{t('drawing.deleteItem')}</span>
            </th>
            {hasComparison ? (
              comparisonColumns.map(column => (
                <th
                  key={column.key}
                  colSpan={column.key === currentColumnKey ? 3 : 2}
                  className="bg-[#A5A5A5] h-[30px] sticky
                  before:content-[''] before:absolute before:inset-0
                  before:border-t before:border-b before:border-r before:border-[#d6d6d6]"
                  style={{
                    ...fixedWidthStyle(getComparisonGroupWidth(column, currentColumnKey)),
                    ...stickyStyle({ top: 0, zIndex: HEADER_STICKY_Z_INDEX }),
                  }}
                >
                  <div className={`flex items-center justify-center w-full h-full ${column.key === currentColumnKey ? 'border-b-[4px] border-[#688CF5]' : ''}`}>
                    {getColumnLabel(column)}
                  </div>
                </th>
              ))
            ) : (
              <>
                {displayDiff?.data?.condition.hasSpec &&
                  <th
                    colSpan={1}
                    className="bg-[#A5A5A5] h-[30px] sticky
                    before:content-[''] before:absolute before:inset-0
                    before:border-t before:border-b before:border-r before:border-[#d6d6d6]"
                    style={{
                      ...fixedWidthStyle(SPEC_COLUMN_WIDTH),
                      ...stickyStyle({ top: 0, zIndex: HEADER_STICKY_Z_INDEX }),
                    }}
                  >
                    <div className="flex items-center justify-center w-full h-full">
                      {rowSample.kind === 'final' ? 'FINAL' : formatOrdinal(rowSample.time)}
                    </div>
                  </th>
                }
                <th
                  colSpan={1}
                  className="bg-[#A5A5A5] h-[30px] sticky
                  before:content-[''] before:absolute before:inset-0
                  before:border-t before:border-b before:border-r before:border-[#d6d6d6]"
                  style={{
                    ...fixedWidthStyle(RESULT_COLUMN_WIDTH),
                    ...stickyStyle({ top: 0, zIndex: HEADER_STICKY_Z_INDEX }),
                  }}
                >
                  <div className={`flex items-center justify-center w-full h-full ${rowSample.time && 'border-b-[4px] border-[#688CF5]'}`}>
                    {rowSample.kind === 'final' ? 'FINAL' : formatOrdinal(rowSample.time)}
                  </div>
                </th>
                <th
                  rowSpan={2}
                  className="bg-[#3E3E3E] sticky
                  before:content-[''] before:absolute before:inset-0
                  before:border-t before:border-b before:border-r before:border-[#adadad]"
                  style={{
                    ...fixedWidthStyle(DIFF_COLUMN_WIDTH),
                    ...stickyStyle({ top: 0, zIndex: HEADER_STICKY_Z_INDEX }),
                  }}
                >
                  <span className={firstRowHeaderLabelClass}>{t('sample.diff')}</span>
                </th>
              </>
            )}
          </tr>
          <tr>
            {hasComparison ? (
              comparisonColumns.map(column => (
                <React.Fragment key={column.key}>
                  <th
                    className="bg-black font-medium sticky
                    before:content-[''] before:absolute before:inset-0
                    before:border-t before:border-b before:border-r before:border-[#adadad]"
                    style={{
                      ...fixedWidthStyle(SPEC_COLUMN_WIDTH),
                      ...stickyStyle({ top: HEADER_FIRST_ROW_HEIGHT, zIndex: HEADER_STICKY_Z_INDEX }),
                    }}
                  >
                    <span className={secondRowHeaderLabelClass}>{t('sample.specDimension')}</span>
                  </th>
                  <th
                    className="bg-black font-medium sticky
                    before:content-[''] before:absolute before:inset-0
                    before:border-t before:border-b before:border-r before:border-[#adadad]"
                    style={{
                      ...fixedWidthStyle(RESULT_COLUMN_WIDTH),
                      ...stickyStyle({ top: HEADER_FIRST_ROW_HEIGHT, zIndex: HEADER_STICKY_Z_INDEX }),
                    }}
                  >
                    <span className={secondRowHeaderLabelClass}>{t('sample.measurementResult')}</span>
                  </th>
                  {column.key === currentColumnKey && (
                    <th
                      className="bg-black font-medium sticky
                      before:content-[''] before:absolute before:inset-0
                      before:border-t before:border-b before:border-r before:border-[#adadad]"
                      style={{
                        ...fixedWidthStyle(DIFF_COLUMN_WIDTH),
                        ...stickyStyle({ top: HEADER_FIRST_ROW_HEIGHT, zIndex: HEADER_STICKY_Z_INDEX }),
                      }}
                    >
                      <span className={secondRowHeaderLabelClass}>{t('sample.diff')}</span>
                    </th>
                  )}
                </React.Fragment>
              ))
            ) : (
              <>
                {displayDiff?.data?.condition.hasSpec &&
                  <th
                    className="bg-black font-medium sticky
                    before:content-[''] before:absolute before:inset-0
                    before:border-t before:border-b before:border-r before:border-[#adadad]"
                    style={{
                      ...fixedWidthStyle(SPEC_COLUMN_WIDTH),
                      ...stickyStyle({ top: HEADER_FIRST_ROW_HEIGHT, zIndex: HEADER_STICKY_Z_INDEX }),
                    }}
                  >
                    <span className={secondRowHeaderLabelClass}>{t('sample.specDimension')}</span>
                  </th>
                }
                <th
                  className="bg-black font-medium sticky
                  before:content-[''] before:absolute before:inset-0
                  before:border-t before:border-b before:border-r before:border-[#adadad]"
                  style={{
                    ...fixedWidthStyle(RESULT_COLUMN_WIDTH),
                    ...stickyStyle({ top: HEADER_FIRST_ROW_HEIGHT, zIndex: HEADER_STICKY_Z_INDEX }),
                  }}
                >
                  <span className={secondRowHeaderLabelClass}>{t('sample.measurementResult')}</span>
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {tableData.map((el, index) => {
            const isDrawingRow = isMeasurementRowDrawing(
              manualDrawingIndex,
              el.data_measure_index,
              el.candidate_measure_index
            )
            const rowBackgroundClass = getTableRowBackgroundClass(index, isDrawingRow)
            const isDimmedRow = shouldDimMeasurementRow({
              hasMultipleSampleImages,
              selectedImage,
              rowImageIndex: el.image_index,
              hasCurrentMeasurement: el.has_current_measurement,
            })
            const dimmedTextClass = isDimmedRow ? 'text-[#666666]' : ''
            return (
              <tr key={el.row_key} className={rowBackgroundClass} >
                <td
                  className={clsx("text-center", rowBackgroundClass, dimmedTextClass)}
                  style={{
                    ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.no),
                    ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.no, zIndex: FROZEN_BODY_STICKY_Z_INDEX }),
                    height: ROW_HEIGHT,
                    ...FIRST_BODY_CELL_BORDER_STYLE,
                  }}
                >
                  {formatNumber(index + 1)}
                </td>
                <td
                  style={{
                    ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.color),
                    ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.color, zIndex: FROZEN_BODY_STICKY_Z_INDEX }),
                    backgroundColor: el.color,
                    ...BODY_CELL_BORDER_STYLE,
                  }}
                />
                <td
                  style={{
                    ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.name),
                    ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.name, zIndex: FROZEN_BODY_STICKY_Z_INDEX }),
                    ...BODY_CELL_BORDER_STYLE,
                  }}
                  className={clsx("text-center", rowBackgroundClass, dimmedTextClass, el.measure_type === 'auto' ? 'cursor-default' : 'cursor-text')}
                  onClick={() => onNameClick(el)}
                >
                  {editingRowKey === el.row_key ? (
                    <input
                      autoFocus
                      className="w-full bg-gray-50 text-black text-center"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => { applyNameChange(el, editingName) }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") applyNameChange(el, editingName)
                        if (e.key === "Escape") setEditingRowKey(null)
                      }}
                    />
                  ) : (el.measure_name)}
                </td>
                <td
                  className={rowBackgroundClass}
                  style={{
                    ...fixedWidthStyle(FROZEN_COLUMN_WIDTHS.delete),
                    ...stickyStyle({ left: FROZEN_COLUMN_LEFTS.delete, zIndex: FROZEN_BODY_STICKY_Z_INDEX }),
                    ...BODY_CELL_BORDER_STYLE,
                  }}
                >
                  {((hasComparison ? el.has_current_measurement : (el.measure_name || el.result_real)) && canEdit) ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <button
                        className={clsx("w-[50px] h-[25px] rounded-[5px] bg-white text-black text-[12px]", !canEdit && 'cursor-not-allowed', isDimmedRow && 'opacity-45')}
                        onClick={() => handleDeleteClick(el)}
                        aria-disabled={!canEdit}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  ) : ''}
                </td>
                {hasComparison ? (
                  el.comparison_cells.map(cell => (
                    <React.Fragment key={cell.column_key}>
                      <td
                        style={{
                          ...fixedWidthStyle(SPEC_COLUMN_WIDTH),
                          ...BODY_CELL_BORDER_STYLE,
                        }}
                        className={clsx("text-center", dimmedTextClass)}
                      >
                        {formatNullableValue(cell.spec)}
                      </td>
                      <td
                        style={{
                          ...fixedWidthStyle(RESULT_COLUMN_WIDTH),
                          ...BODY_CELL_BORDER_STYLE,
                        }}
                        className={clsx("text-center", dimmedTextClass, cell.is_current && canEdit && 'cursor-pointer')}
                        onClick={() => {
                          if (cell.is_current) {
                            handleResultCellClick(el)
                          }
                        }}
                      >
                        {formatNullableValue(cell.result_real) || (cell.is_current && isDrawingRow ? t('drawing.moving') : '')}
                      </td>
                      {cell.is_current && (
                        <td
                          style={{
                            ...fixedWidthStyle(DIFF_COLUMN_WIDTH),
                            ...BODY_CELL_BORDER_STYLE,
                          }}
                          className={`text-center ${cell.diff?.toString().startsWith('-') ? 'first-letter:tracking-[3px]' : ''}`}
                        >
                          <span className={clsx(isDimmedRow ? 'text-[#666666]' : (cell.validate === 'OK' || cell.diff === '-' || cell.diff === null || isZeroDiffValue(String(cell.diff)) ? 'text-white' : 'text-[#FF0000]'))}>
                            {cell.diff ?? '-'}
                          </span>
                        </td>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    {displayDiff?.data?.condition.hasSpec &&
                      <td
                        style={{
                          ...fixedWidthStyle(SPEC_COLUMN_WIDTH),
                          ...BODY_CELL_BORDER_STYLE,
                        }}
                        className={clsx("text-center", dimmedTextClass)}
                      >
                        {el.spec !== 0 ? el.spec : ''}
                      </td>
                    }
                    <td
                      style={{
                        ...fixedWidthStyle(RESULT_COLUMN_WIDTH),
                        ...BODY_CELL_BORDER_STYLE,
                      }}
                      className={clsx("text-center", dimmedTextClass)}
                      onClick={() => handleResultCellClick(el)}
                    >
                      {el.result_real !== 0 ? el.result_real : isDrawingRow ? t('drawing.moving') : ''}
                    </td>
                    <td
                      style={{
                        ...fixedWidthStyle(DIFF_COLUMN_WIDTH),
                        ...BODY_CELL_BORDER_STYLE,
                      }}
                      className={`text-center ${el.diff?.toString().startsWith('-') ? 'first-letter:tracking-[3px]' : ''}`}
                    >
                      <span className={clsx(isDimmedRow ? 'text-[#666666]' : (el.validate === 'OK' || el.diff === '-' || isZeroDiffValue(el.diff) ? 'text-white' : 'text-[#FF0000]'))}>{el.diff}</span>
                    </td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      <DeleteModal isModalOpen={isModalOpen} closeModal={closeModal} deleteMeasureInfo={deleteMeasure} hasComparison={hasComparison} />
      <DuplicateModal isModalOpen={confirmOpen} closeModal={onConfirmCancel} pending={pending} onConfirmOK={onConfirmOK} />
      <AutoEditConfirmModal isModalOpen={isAutoConfirmOpen} closeModal={onAutoConfirmCancel} onConfirmOK={onAutoConfirmOK} />
    </div>
  )
}

function DeleteModal({ isModalOpen, closeModal, deleteMeasureInfo, hasComparison }: { isModalOpen: boolean, closeModal: () => void, deleteMeasureInfo?: DataType, hasComparison: boolean }) {
  const { t } = useTranslation()
  const { rowSample } = useContext(SamplesContext)
  const { updateRowSample } = useContext(UpdateSamplesContext)

  // delete:最初の確認 confirm:復活確認
  const [stage, setStage] = useState<'delete' | 'confirm'>('delete')

  const handleCloseAll = useCallback(() => {
    setStage('delete')
    closeModal()
  }, [closeModal])

  // 1回目の「削除しますか？」でOKしたとき
  const handleFirstOk = useCallback(() => {
    if (!deleteMeasureInfo || deleteMeasureInfo.data_measure_index === null) return
    const { measure_name, measure_type, data_measure_index: measureIndex } = deleteMeasureInfo

    // 手動(manual)かつ、同名の自動(auto)が論理削除済みか探す
    const hasDeletedAuto = rowSample.sampleMeasurements?.some(
      sm =>
        sm.measure_name === measure_name &&
        sm.measure_type === 'auto' &&
        sm.is_deleted
    )

    // manual:物理削除 auto:論理削除
    let updated = [...rowSample.sampleMeasurements]
    if (measure_type === 'manual') {
      updated = updated
        .filter(sm => !(sm.measure_name === measure_name && sm.measure_type === 'manual' && sm.measure_index === measureIndex))
        .map((m) => !hasComparison && measureIndex < m.measure_index ? { ...m, measure_index: m.measure_index - 1 } : m)
    } else {
      updated = updated.map(sm => sm.measure_name === measure_name ? { ...sm, is_deleted: true } : sm)
      updated = updated.map(m => !hasComparison && measureIndex < m.measure_index ? { ...m, measure_index: m.measure_index - 1 } : m)
    }

    updateRowSample({ sampleMeasurements: updated })

    // 同名auto復活確認が必要ならステージを進める
    if (measure_type === 'manual' && hasDeletedAuto) {
      setStage('confirm')
      return
    }

    handleCloseAll()
  }, [deleteMeasureInfo, rowSample, updateRowSample, handleCloseAll, hasComparison])

  const handleSecondOk = useCallback(() => {
    if (!deleteMeasureInfo) return
    const { measure_name } = deleteMeasureInfo

    const restoreDataIdx = rowSample.sampleMeasurements.find(m => m.measure_name === measure_name && m.measure_type === 'auto' && m.is_deleted)?.measure_index

    // 同名の自動(auto)を復活させる
    const restored = rowSample.sampleMeasurements
      .map(sm => {
        const nameMatch = sm.measure_name.trim() === measure_name.trim()
        const isTargetAutoDeleted = nameMatch && sm.measure_type === 'auto' && sm.is_deleted

        if (isTargetAutoDeleted) {
          return {
            ...sm,
            measure_index: sm.measure_index,
            is_deleted: false,
          }
        }
        if (!hasComparison && sm.measure_index >= (restoreDataIdx || 0)) {
          return {
            ...sm,
            measure_index: sm.measure_index + 1
          }
        }
        return sm
      })

    updateRowSample({ sampleMeasurements: restored })
    handleCloseAll()
  }, [deleteMeasureInfo, rowSample, updateRowSample, handleCloseAll, hasComparison])

  const handleCancel = useCallback(() => {
    handleCloseAll()
  }, [handleCloseAll])

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={closeModal} className="flex min-h-[189px] w-[calc(100vw-32px)] max-w-[500px] flex-col items-center justify-center px-[24px] py-[24px]">
        <span className="w-full break-words text-center text-black">{t('messages.deleteMeasurementItemConfirm')}</span>
        <div className="mt-[15px] flex w-full max-w-[328px] gap-[8px]">
          <button
            onClick={handleCancel}
            className="h-[47px] min-w-0 flex-1 rounded-[50px] border-[1.5px] border-[#C5C7FF] font-bold text-[#545899]"
          >
            {t('common.cancel')}
          </button>
          <button
            className="h-[47px] min-w-0 flex-1 rounded-[50px] bg-[#C5C7FF] font-bold text-[#545899]"
            onClick={handleFirstOk}
          >
            {t('common.delete')}
          </button>
        </div>
      </Modal>
      {isModalOpen && stage === 'confirm' && (
        <Modal isOpen={true} onClose={handleCancel} className="flex min-h-[189px] w-[calc(100vw-32px)] max-w-[500px] flex-col items-center justify-center px-[24px] py-[24px]">
          <span className="w-full break-words text-center text-black">{t('messages.restoreDeletedAutoMeasurementConfirm')}</span>
          <div className="mt-[15px] flex w-full max-w-[328px] gap-[8px]">
            <button
              onClick={handleCancel}
              className="h-[47px] min-w-0 flex-1 rounded-[50px] border-[1.5px] border-[#C5C7FF] font-bold text-[#545899]"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSecondOk}
              className="h-[47px] min-w-0 flex-1 rounded-[50px] bg-[#C5C7FF] font-bold text-[#545899]"
            >
              OK
            </button>
          </div>
        </Modal>
      )}
    </>
  )
}

function DuplicateModal({
  isModalOpen,
  closeModal,
  pending,
  onConfirmOK
}: {
  isModalOpen: boolean,
  closeModal: () => void,
  pending: { index: number, newName: string, duplicateIdx: number, duplicateType: "auto" | "manual" | "" } | null,
  onConfirmOK: () => void,
}) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} className="flex flex-col items-center justify-center min-w-[414px] h-[189px] px-[25px]">
      <span className="text-black text-center">
        <p>{t('messages.measurementItemAlreadyExists', { name: pending?.newName ?? '' })}</p>
        <p>{t('messages.overwriteConfirm')}</p>
      </span>
      <div className="space-x-[8px] mt-[15px]">
        <button
          onClick={closeModal}
          className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={onConfirmOK}
          className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          OK
        </button>
      </div>
    </Modal>
  )
}

function AutoEditConfirmModal({
  isModalOpen,
  closeModal,
  onConfirmOK
}: {
  isModalOpen: boolean,
  closeModal: () => void,
  onConfirmOK: () => void
}) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isModalOpen} onClose={closeModal} className="flex flex-col items-center justify-center min-w-[414px] h-[189px] px-[25px]">
      <span className="text-black text-center">
        {t('messages.autoMeasurementOverwriteConfirm').split('\n').map((line) => (
          <p key={line}>{line}</p>
        ))}
      </span>
      <div className="space-x-[8px] mt-[15px]">
        <button
          onClick={closeModal}
          className="w-[160px] h-[47px] border-[1.5px] border-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={onConfirmOK}
          className="w-[160px] h-[47px] bg-[#C5C7FF] rounded-[50px] text-[#545899] font-bold"
        >
          OK
        </button>
      </div>
    </Modal>
  )
}
